<template>
  <div>
    <div class="header-section">
      <div class="content-section">
        <h3 class="section-title">{{ $t('contacts.title') }}</h3>

        <!-- Export Section -->
        <div class="content mb-6">
          <h4 class="text-lg font-semibold mb-3">{{ $t('contacts.exportUserData') }}</h4>
          <button @click="exportData" class="BTNMAIN py-2 px-4 flex items-center gap-2">
            <i class="icon-smsir-download text-xl"></i>
            <span>{{ $t('contacts.exportButton') }}</span>
          </button>
          <p class="text-sm text-gray-500 mt-2">{{ $t('contacts.exportDescription') }}</p>
        </div>

        <!-- Bulk SMS Section -->
        <div class="content">
          <h4 class="text-lg font-semibold mb-3">{{ $t('contacts.bulkSms') }}</h4>
          
          <div class="flex flex-col md:flex-row gap-6">

             <div class="w-full md:w-7/12 lg:w-8/12">
              <div v-if="loadingContacts" class="text-center py-4">
                <LoadingSpinner />
                <p>{{ $t('contacts.loadingContacts') }}</p>
              </div>
              <div v-else-if="contacts.length === 0" class="no-data p-4 text-center bg-gray-50 rounded-lg">
                {{ $t('contacts.noContactsFound') }}
              </div>
              <div v-else>
                <div class="mb-4">
                  <input 
                    type="text" 
                    v-model="searchTerm" 
                    :placeholder="$t('contacts.searchPlaceholder')" 
                    class="input-main w-full"
                  />
                </div>
                <div class="overflow-x-auto mb-4 border border-gray-200 rounded-lg max-h-[60vh]">
                  <table class="w-full min-w-[400px]">
                    <thead class="sticky top-0 bg-gray-100 z-10">
                      <tr >
                        <th class="p-3 text-right w-10">
                          <input type="checkbox" @change="toggleSelectAll" :checked="allSelected" class="ml-2 border border-[#D9D9D9] rounded-[7px] size-[20px]" />
                        </th>
                        <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('contacts.name') }}</th>
                        <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('contacts.phone') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="contact in paginatedContacts" :key="contact.id" class="border-b hover:bg-gray-50">
                        <td class="p-3">
                          <input type="checkbox" :value="contact.id" v-model="selectedContacts" class="ml-2 border border-[#D9D9D9] rounded-[7px] size-[20px]" />
                        </td>
                        <td class="p-3 text-base text-[#0D0D0D]">{{ contact.name }}</td>
                        <td class="p-3 text-base text-[#0D0D0D]">{{ contact.phone }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="pagination sticky bottom-0 bg-white py-3" v-if="totalPages > 1">
                  <button
                    @click="goToPage(currentPage - 1)"
                    :disabled="currentPage === 1"
                    class="px-3 py-1 border rounded"
                    :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
                  >
                    {{ $t('common.previous') }}
                  </button>
                  <span class="px-3 py-1">
                    {{ currentPage }} / {{ totalPages }}
                  </span>
                  <button
                    @click="goToPage(currentPage + 1)"
                    :disabled="currentPage >= totalPages"
                    class="px-3 py-1 border rounded"
                    :class="{ 'opacity-50 cursor-not-allowed': currentPage >= totalPages }"
                  >
                    {{ $t('common.next') }}
                  </button>
                </div>
              </div>
            </div>
            <div class="w-full md:w-5/12 lg:w-4/12">
              <div class="sticky top-6">
                <label class="block text-[#4D4D4D] text-sm mb-1">{{ $t('contacts.message') }}</label>
                <textarea 
                  v-model="bulkMessage" 
                  :placeholder="$t('contacts.enterBulkMessage')" 
                  rows="8" 
                  class="input-main-textarea w-full mb-3"
                ></textarea>
                <button 
                  @click="sendBulkSms" 
                  class="BTNMAIN bg-[#12BA6C] py-2 px-6 flex items-center justify-center w-full gap-2" 
                  :disabled="selectedContacts.length === 0 || !bulkMessage.trim() || sendingBulkSms"
                  :class="{ 'opacity-70 cursor-not-allowed': selectedContacts.length === 0 || !bulkMessage.trim() || sendingBulkSms }"
                >
                  <i class="icon-smsir-send text-xl text-white"></i>
                  <span v-if="sendingBulkSms">{{ $t('contacts.sending') }}</span>
                  <span v-else class="text-white text-base">{{ $t('contacts.sendBulkSmsButton') }} ({{ selectedContacts.length }})</span>
                </button>
              </div>
            </div>

           
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx'; // Import XLSX library
import LoadingSpinner from '../LoadingSpinner.vue'; // Assuming you have this component

export default {
  name: 'ContactsTab',
  components: {
    LoadingSpinner
  },
  data() {
    return {
      loadingContacts: false,
      sendingBulkSms: false,
      contacts: [], // Format: { id, name, phone }
      selectedContacts: [],
      bulkMessage: '',
      searchTerm: '',
      currentPage: 1,
      perPage: 20,
  
    };
  },
  computed: {
    filteredContacts() {
      if (!this.searchTerm) {
        return this.contacts;
      }
      const lowerSearchTerm = this.searchTerm.toLowerCase();
      return this.contacts.filter(contact => 
        (contact.name && contact.name.toLowerCase().includes(lowerSearchTerm)) ||
        (contact.phone && contact.phone.toLowerCase().includes(lowerSearchTerm))
      );
    },
    paginatedContacts() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredContacts.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredContacts.length / this.perPage);
    },
    allSelected() {
      return this.filteredContacts.length > 0 && this.selectedContacts.length === this.filteredContacts.length;
    }
  },
  methods: {
    async fetchContacts() {
      this.loadingContacts = true;
      try {
        const response = await axios.post(window.sms_ir_login.ajaxUrl, new URLSearchParams({
          action: 'smsir_get_contacts', // Needs backend implementation
          nonce: window.sms_ir_login.nonce,
        }));
        if (response.data.success) {
          this.contacts = response.data.data.map(c => ({ ...c, id: String(c.id) })); // Ensure ID is string for v-model
        } else {
          this.contacts = []; // Fallback for demo
          // Swal.fire({ text: this.$t('contacts.errorLoadingContacts'), icon: 'error', toast: true, position: 'top-start', showConfirmButton: false, timer: 3000 });
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
        this.contacts = []; // Fallback for demo
        // Swal.fire({ text: this.$t('contacts.errorLoadingContacts'), icon: 'error', toast: true, position: 'top-start', showConfirmButton: false, timer: 3000 });
      } finally {
        this.loadingContacts = false;
      }
    },
    async exportData() {
      // Show a loading state or a message to the user
      Swal.fire({
        title: this.$t('contacts.exportingTitle', { default: 'Exporting Contacts' }),
        text: this.$t('contacts.exportingPleaseWait', { default: 'Please wait while your data is being prepared...' }),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        // Fetch ALL contacts for export - we can reuse ajax_get_contacts
        // If ajax_get_contacts supports pagination, we might need to loop or fetch all in one go for export.
        // For now, assume it returns all contacts or a reasonable amount for client-side export.
        const response = await axios.post(window.sms_ir_login.ajaxUrl, new URLSearchParams({
          action: 'smsir_get_contacts', 
          nonce: window.sms_ir_login.nonce,
          // Potentially add a parameter like 'export_all: true' if pagination is involved
        }));

        if (response.data.success && response.data.data) {
          const contactsToExport = response.data.data;

          if (contactsToExport.length === 0) {
            Swal.fire(this.$t('contacts.noDataToExport', { default: 'No Contacts' }), this.$t('contacts.noContactsAvailableForExport', { default: 'There are no contacts available to export.' }), 'info');
            return;
          }

          // Prepare data for worksheet
          const sheetData = contactsToExport.map((contact, index) => {
            const row = {
              '#': index + 1,
              [this.$t('contacts.nameHeader', { default: 'Name' })]: contact.name,
              [this.$t('contacts.emailHeader', { default: 'Email' })]: contact.email, // Email is now fetched
              [this.$t('contacts.phoneHeader', { default: 'Phone' })]: contact.phone,
            };

            // Add custom fields dynamically
            if (contact.custom_fields && Array.isArray(contact.custom_fields)) {
              contact.custom_fields.forEach(field => {
                row[field.label] = field.value; // Use label as column header
              });
            }
            return row;
          });

          const ws = XLSX.utils.json_to_sheet(sheetData);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, this.$t('contacts.sheetName', { default: 'Contacts' }));
          XLSX.writeFile(wb, 'smsir-contacts-' + new Date().toISOString().slice(0,10) + '.xlsx');
          
          Swal.close(); // Close the loading Swal
          Swal.fire({
            text: this.$t('contacts.exportSuccess', { default: 'Contacts exported successfully!' }),
            icon: 'success',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000
          });

        } else {
          throw new Error(response.data.data || this.$t('contacts.errorFetchingForExport', { default: 'Failed to fetch contacts for export.' }));
        }
      } catch (error) {
        console.error('Error exporting contacts:', error);
        Swal.fire(
          this.$t('common.error', { default: 'Error' }), 
          error.message || this.$t('contacts.exportFailedError', { default: 'An error occurred during export.' }), 
          'error'
        );
      } 
    },
    async sendBulkSms() {
      if (this.selectedContacts.length === 0 || !this.bulkMessage.trim()) {
        Swal.fire(this.$t('common.error'), this.$t('contacts.errorSelectContactsAndMessage'), 'error');
        return;
      }
      this.sendingBulkSms = true;
      try {
        const response = await axios.post(window.sms_ir_login.ajaxUrl, new URLSearchParams({
          action: 'smsir_send_bulk_sms', // Needs backend implementation
          nonce: window.sms_ir_login.nonce,
          contact_ids: JSON.stringify(this.selectedContacts),
          message: this.bulkMessage,
        }));

        if (response.data.success) {
          Swal.fire({
            text: this.$t('contacts.bulkSmsSuccess'),
            icon: 'success',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000,
          });
          this.selectedContacts = [];
          this.bulkMessage = '';
        } else {
          Swal.fire(this.$t('common.error'), response.data.data || this.$t('contacts.errorSendingBulkSms'), 'error');
        }
      } catch (error) {
        console.error('Error sending bulk SMS:', error);
        Swal.fire(this.$t('common.error'), this.$t('contacts.errorSendingBulkSms'), 'error');
      } finally {
        this.sendingBulkSms = false;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedContacts = this.filteredContacts.map(c => c.id);
      } else {
        this.selectedContacts = [];
      }
    }
  },
  watch: {
    searchTerm() {
      this.currentPage = 1; // Reset to first page on search
    }
  },
  mounted() {
    this.fetchContacts();
    // MOCK DATA FOR UI DEVELOPMENT - REMOVE WHEN BACKEND IS READY
    // setTimeout(() => {
    //   this.contacts = [
    //     { id: '1', name: 'John Doe', phone: '09123456789' },
    //     { id: '2', name: 'Jane Smith', phone: '09123456780' },
    //     { id: '3', name: 'Alice Johnson', phone: '09123456781' },
    //     { id: '4', name: 'Bob Williams', phone: '09123456782' },
    //     { id: '5', name: 'Charlie Brown', phone: '09123456783' },
    //     { id: '6', name: 'David Davis', phone: '09123456784' },
    //     { id: '7', name: 'Eve Evans', phone: '09123456785' },
    //     { id: '8', name: 'Frank Foster', phone: '09123456786' },
    //     { id: '9', name: 'Grace Green', phone: '09123456787' },
    //     { id: '10', name: 'Henry Hill', phone: '09123456788' },
    //     { id: '11', name: 'Ivy Irving', phone: '09123456790' },
    //   ];
    //   this.loadingContacts = false;
    // }, 1000);
  }
};
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}
/* Add other styles as needed */
</style> 